/**
 * Hent ansatte (#294 ONB-04..06; onboarding handoff screens 03 and 04): choose where the people are
 * today, see them before anything is added, add the chosen ones, see each person's result.
 *
 * - Excel or CSV: the file is read and checked on the server (POST /admin/people-import/preview), which
 *   writes nothing. Avbryt before «Legg til» adds nobody.
 * - The chosen people are made one by one through POST /admin/users, so every rule of Ny ansatt holds
 *   and one refusal does not stop the rest. «Prøv igjen» reads the file again first: who was made is
 *   then «Finnes fra før», so a retry cannot make anyone twice.
 * - The payroll system's own import (PowerOffice Go, Tripletex) stays where it is, under Innstillinger →
 *   Lønn; manual entry is Ny ansatt. No provider logos: the Visma terms forbid Visma's
 *   (docs/integrations/poweroffice.md).
 *
 * Adding people adds people: no hours, no invitations, no approvals (the note under the result).
 */
import { api, errorText, post } from '@/api/client';
import { T } from '@/i18n';
import { useTenant } from '@/shell/tenant';
import { Button } from '@/ui';
import { Icon, type IconName } from '@/ui/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import s from './importer.module.css';

export interface FilePerson {
  row: number;
  name: string;
  lastname: string;
  email: string | null;
  phone: string | null;
  employee_id: string | null;
  birth_date: string | null;
  employment_start: string | null;
  agreed_week_hours: number | null;
  status: 'new' | 'exists' | 'refused';
  problems: string[];
  existing: { user_id: number; name: string } | null;
}
export interface FileRead {
  source: 'csv' | 'xlsx';
  columns: Record<string, string>;
  people: FilePerson[];
  counts: { new: number; exists: number; refused: number };
}
/** made; already there; refused by the file itself (a retry cannot help); failed when saved (a retry may) */
type Outcome = { person: FilePerson; result: 'made' | 'exists' | 'refused' | 'failed'; reason?: string };

export function readPeopleFile(file: File) {
  const fd = new FormData();
  fd.append('file', file);
  return api<FileRead>('/admin/people-import/preview', { method: 'POST', body: fd });
}
// the username Ny ansatt would be typed with: the address, else name, surname and the file's row
const userOf = (p: FilePerson) =>
  (p.email ?? `${p.name}.${p.lastname}.${p.row}`).toLowerCase().replace(/\s+/g, '');
const keyOf = (p: FilePerson) => `${p.row}`;

/** Makes the chosen people one by one; a refusal is that person's result, not the end of the run. */
export async function addPeople(people: FilePerson[]): Promise<Outcome[]> {
  const out: Outcome[] = [];
  for (const p of people) {
    try {
      await post('/admin/users', {
        username: userOf(p),
        name: p.name,
        lastname: p.lastname,
        email: p.email ?? '',
        phone: p.phone ?? '',
        birth_date: p.birth_date,
        ...(p.employee_id ? { employee_id: p.employee_id } : {}),
        ...(p.employment_start ? { employment_start: p.employment_start } : {}),
        ...(p.agreed_week_hours != null ? { agreed_week_hours: p.agreed_week_hours } : {}),
        // nobody gets mail here: inviting is its own step (ONB-11)
        send_welcome: false,
      });
      out.push({ person: p, result: 'made' });
    } catch (e) {
      out.push({ person: p, result: 'failed', reason: errorText(e) });
    }
  }
  return out;
}

const SYSTEM_NAME = { poweroffice: 'PowerOffice Go', tripletex: 'Tripletex' } as const;

export default function Importer() {
  const { tenant } = useTenant();
  const qc = useQueryClient();
  const sys =
    tenant?.payroll_system === 'poweroffice' || tenant?.payroll_system === 'tripletex'
      ? tenant.payroll_system
      : null;
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [read, setRead] = useState<FileRead | null>(null);
  const [pick, setPick] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<'read' | 'add' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Outcome[] | null>(null);

  const load = async (f: File) => {
    setBusy('read');
    setError(null);
    try {
      const r = await readPeopleFile(f);
      setFile(f);
      setRead(r);
      setPick(new Set(r.people.filter((p) => p.status === 'new').map(keyOf)));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  const cancel = () => {
    setFile(null);
    setRead(null);
    setDone(null);
    setError(null);
    if (input.current) input.current.value = '';
  };
  const add = async (people: FilePerson[]) => {
    setBusy('add');
    const out = await addPeople(people);
    // the rest of the file as it stood: who was already there, who could not be added
    const others: Outcome[] = (read?.people ?? [])
      .filter((p) => !people.some((x) => keyOf(x) === keyOf(p)))
      .filter((p) => p.status !== 'new')
      .map((p) => ({
        person: p,
        result: p.status === 'exists' ? 'exists' : 'refused',
        reason: p.problems.join(' '),
      }));
    setDone([...out, ...others]);
    setBusy(null);
    qc.invalidateQueries({ queryKey: ['users'] });
    qc.invalidateQueries({ queryKey: ['onboarding'] });
  };
  // a retry reads the file again first, so whoever was made in the meantime is not made twice
  const retry = async () => {
    if (!file || !done) return;
    const failed = new Set(done.filter((o) => o.result === 'failed').map((o) => keyOf(o.person)));
    setBusy('add');
    try {
      const r = await readPeopleFile(file);
      setRead(r);
      await add(r.people.filter((p) => p.status === 'new' && failed.has(keyOf(p))));
    } catch (e) {
      setError(errorText(e));
      setBusy(null);
    }
  };

  return (
    <div className={s.page} data-testid="importer">
      <Link className={s.back} to="/kom-i-gang">
        <Icon name="chevronLeft" size={16} />
        {T('Tilbake til Kom i gang')}
      </Link>
      <header className={s.head}>
        <span className={s.mark} aria-hidden="true">
          <Icon name="users" size={22} />
        </span>
        <div>
          <h1>{T('Hent ansatte')}</h1>
          <p>{T('Bruk opplysningene dere allerede har. Dere ser dem før noe legges til.')}</p>
        </div>
      </header>

      {done ? (
        <Result
          done={done}
          busy={busy === 'add'}
          company={tenant?.name ?? ''}
          onRetry={retry}
          onAgain={cancel}
        />
      ) : read ? (
        <Preview
          read={read}
          pick={pick}
          setPick={setPick}
          busy={busy === 'add'}
          onAdd={() => add(read.people.filter((p) => p.status === 'new' && pick.has(keyOf(p))))}
          onCancel={cancel}
        />
      ) : (
        <section aria-labelledby="imp-src">
          <h2 id="imp-src" className={s.h2}>
            {T('Hvor har dere de ansatte i dag?')}
          </h2>
          <ul className={s.sources}>
            <li>
              <Source
                icon="upload"
                title={T('Excel eller CSV')}
                line={T('En liste med navn, fødselsdato og gjerne e-post, ansattnummer og arbeidstid.')}
              >
                <input
                  ref={input}
                  type="file"
                  accept=".xlsx,.csv,.txt"
                  className="sr-only"
                  id="imp-file"
                  data-testid="imp-file"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) load(f);
                  }}
                />
                <label htmlFor="imp-file" className="btn pri" aria-disabled={busy === 'read'}>
                  {busy === 'read' ? T('Leser filen …') : T('Velg fil')}
                </label>
              </Source>
            </li>
            {sys && (
              <li>
                <Source
                  icon="banknote"
                  title={T('Fra {system}', { system: SYSTEM_NAME[sys] })}
                  line={T('Hent ansatte fra lønnssystemet dere bruker, under Innstillinger → Lønn.')}
                >
                  <Link className="btn" to={`/innstillinger/lonn#${sys}`}>
                    {T('Gå til {system}', { system: SYSTEM_NAME[sys] })}
                  </Link>
                </Source>
              </li>
            )}
            <li>
              <Source
                icon="userPlus"
                title={T('Legg inn selv')}
                line={T('Én og én, med de opplysningene du har.')}
              >
                <Link className="btn" to="/ansatte/ny">
                  {T('Ny ansatt')}
                </Link>
              </Source>
            </li>
          </ul>
          {error && (
            <p className={s.error} role="alert" data-testid="imp-error">
              {error}
            </p>
          )}
          <p className={s.hint}>
            {T(
              'Første rad i filen må ha overskrifter, for eksempel Fornavn, Etternavn, E-post, Ansattnr, Fødselsdato, Ansatt fra og Avtalt arbeidstid.',
            )}
          </p>
        </section>
      )}
    </div>
  );
}

function Source({
  icon,
  title,
  line,
  children,
}: { icon: IconName; title: string; line: string; children: React.ReactNode }) {
  return (
    <div className={s.source}>
      <span className={s.sIcon} aria-hidden="true">
        <Icon name={icon} size={20} />
      </span>
      <span className={s.sText}>
        <b>{title}</b>
        <span>{line}</span>
      </span>
      <span className={s.sAct}>{children}</span>
    </div>
  );
}

const STATUS = {
  new: 'Ny',
  exists: 'Finnes fra før',
  refused: 'Kan ikke legges til',
} as const;

function Preview({
  read,
  pick,
  setPick,
  busy,
  onAdd,
  onCancel,
}: {
  read: FileRead;
  pick: Set<string>;
  setPick: (s: Set<string>) => void;
  busy: boolean;
  onAdd: () => void;
  onCancel: () => void;
}) {
  const chosen = read.people.filter((p) => p.status === 'new' && pick.has(keyOf(p))).length;
  const toggle = (k: string) => {
    const n = new Set(pick);
    if (n.has(k)) n.delete(k);
    else n.add(k);
    setPick(n);
  };
  return (
    <section aria-labelledby="imp-prev" data-testid="imp-preview">
      <h2 id="imp-prev" className={s.h2}>
        {T('Se over før noe legges til')}
      </h2>
      <p className={s.sum}>
        {T('{n} nye, {e} finnes fra før, {r} kan ikke legges til.', {
          n: read.counts.new,
          e: read.counts.exists,
          r: read.counts.refused,
        })}{' '}
        {T('Kolonner lest: {c}.', { c: Object.values(read.columns).join(', ') })}
      </p>
      <ul className={s.people}>
        {read.people.map((p) => (
          <li key={keyOf(p)} data-testid={`imp-row-${p.row}`} data-status={p.status}>
            {p.status === 'new' ? (
              <input
                type="checkbox"
                aria-label={T('Legg til {name}', { name: `${p.name} ${p.lastname}` })}
                checked={pick.has(keyOf(p))}
                onChange={() => toggle(keyOf(p))}
              />
            ) : (
              <span className={s.noBox} aria-hidden="true" />
            )}
            <span className={s.pText}>
              <b>
                {p.name} {p.lastname}
              </b>
              <span>
                {[
                  p.email,
                  p.employee_id && T('Ansattnr {n}', { n: p.employee_id }),
                  p.agreed_week_hours != null
                    ? T('{h} t/uke', { h: String(p.agreed_week_hours).replace('.', ',') })
                    : T('Avtalt arbeidstid mangler'),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              {p.status === 'exists' && p.existing && (
                <span>{T('Samme person som {name}.', { name: p.existing.name })}</span>
              )}
              {p.problems.length > 0 && <span className={s.why}>{p.problems.join(' ')}</span>}
            </span>
            <span className={`${s.tag} ${s[p.status]}`}>{T(STATUS[p.status])}</span>
          </li>
        ))}
      </ul>
      <div className={s.foot}>
        <Button tone="pri" disabled={!chosen || busy} onClick={onAdd} data-testid="imp-add">
          {busy ? T('Legger til …') : T('Legg til {n} ansatte', { n: chosen })}
        </Button>
        <Button ghost onClick={onCancel} disabled={busy} data-testid="imp-cancel">
          {T('Avbryt')}
        </Button>
        <span className={s.hint}>{T('Ingen får e-post nå. Invitasjonen sender du når du vil.')}</span>
      </div>
    </section>
  );
}

const RESULT = {
  made: 'Lagret',
  exists: 'Fantes fra før',
  refused: 'Ikke lagret',
  failed: 'Ikke lagret',
} as const;

function Result({
  done,
  busy,
  company,
  onRetry,
  onAgain,
}: { done: Outcome[]; busy: boolean; company: string; onRetry: () => void; onAgain: () => void }) {
  const made = done.filter((o) => o.result === 'made').length;
  const failed = done.filter((o) => o.result === 'failed').length;
  return (
    <section aria-labelledby="imp-res" data-testid="imp-result">
      <h2 id="imp-res" className={s.h2} aria-live="polite">
        {made === 1 ? T('1 ansatt er lagt til') : T('{n} ansatte er lagt til', { n: made })}
      </h2>
      <ul className={s.people}>
        {done.map((o) => (
          <li key={keyOf(o.person)} data-testid={`imp-out-${o.person.row}`} data-result={o.result}>
            <span className={`${s.rIcon} ${s[o.result]}`} aria-hidden="true">
              <Icon name={o.result === 'failed' || o.result === 'refused' ? 'warn' : 'check'} size={16} />
            </span>
            <span className={s.pText}>
              <b>
                {o.person.name} {o.person.lastname}
              </b>
              {o.result === 'made' && <span>{T('Lagt til i {company}.', { company })}</span>}
              {o.reason && <span className={s.why}>{o.reason}</span>}
            </span>
            <span className={`${s.tag} ${s[o.result]}`}>{T(RESULT[o.result])}</span>
          </li>
        ))}
      </ul>
      {failed > 0 && (
        <div className={s.foot}>
          <Button onClick={onRetry} disabled={busy} data-testid="imp-retry">
            {busy ? T('Prøver igjen …') : T('Prøv igjen for {n} som ikke ble lagret', { n: failed })}
          </Button>
        </div>
      )}
      <div className={s.next}>
        <h3>{T('Ansatte er grunnlaget. Kontroll følger arbeidet.')}</h3>
        <p>
          {T(
            'Importen legger til personer. Den henter ingen timer, inviterer ingen og godkjenner ingenting.',
          )}
        </p>
        <ol className={s.steps}>
          <li>
            <Icon name="users" size={20} aria-hidden="true" />
            <span>
              <b>{T('Fullfør opplysningene')}</b>
              <span>{T('Avklar arbeidstid, regler og hvem som skal registrere.')}</span>
            </span>
          </li>
          <li>
            <Icon name="clock" size={20} aria-hidden="true" />
            <span>
              <b>{T('Registrer faktisk arbeid')}</b>
              <span>{T('Ansatte stempler i Timefly, eller leder registrerer start, slutt og pause.')}</span>
            </span>
          </li>
          <li>
            <Icon name="check" size={20} aria-hidden="true" />
            <span>
              <b>{T('Avklar og godkjenn i Kontroll')}</b>
              <span>
                {T('Rett mangler og se beregningen. Godkjente timer kan deretter bli lønnsgrunnlag.')}
              </span>
            </span>
          </li>
        </ol>
        <div className={s.foot}>
          <Link className="btn pri" to="/ansatte?oppgave=arbeidstid" data-testid="imp-finish">
            {T('Fullfør ansattopplysninger')}
          </Link>
          <Link className={s.link} to="/kom-i-gang">
            {T('Til Kom i gang')}
          </Link>
          <button type="button" className={s.link} onClick={onAgain}>
            {T('Hent fra en annen fil')}
          </button>
        </div>
      </div>
    </section>
  );
}
